import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { Button, CopyToClipboardMinimal, Tooltip } from '@weezevent/nacre';

import Style from './formAnswers.module.css';
import { InfoTextTooltip } from '../../infotext-tooltip/infoTextTooltip';
import { capitalizeFirstLetter, translateCountry } from '../../utils';
import { getWeezFileUrl } from '../../../../../../components/upload/utils';
import moment from 'moment-timezone';

const FIELDS = {
    divider: RowDivider,
    image: RowImage,
    file: RowFile,
    birthdate: RowBirthdate
};

function isNotValidAnswer(answer) {
    return !answer || answer === '';
}

function RowBirthdate({ form_field, answer, locale }) {
    //To ensure the backward compatibility of date of birth fields with Menhir,
    //in input we receive a 'DD/MM/YYYY' format.
    //When we create new date of birth fields in Menhir ,
    //we can process everything in isoString.
    const _answer = React.useMemo(() => {
        if (isNotValidAnswer(answer)) {
            return '';
        }

        let dateObj = moment(answer, 'DD/MM/YYYY');

        if (!dateObj.isValid()) {
            return answer;
        }

        return Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(dateObj.format('YYYY-MM-DD')));
    }, [form_field, answer, locale]);

    return (
        <tr>
            <td className={Style['td-field']}>
                <InfoTextTooltip title={form_field?.get_translated_label(locale.toLowerCase())} />
            </td>
            <td className={Style['td-answer']}>{isNotValidAnswer(_answer) ? <span>{`-`}</span> : <InfoTextTooltip title={_answer} />}</td>
        </tr>
    );
}

function RowFile({ form_field, answer, locale }) {
    const { t } = useTranslation();

    const fileUrl = React.useMemo(() => {
        return getWeezFileUrl(answer, window.weezConfig.aws.weez_bucket_form);
    }, [answer]);

    return (
        <tr>
            <td className={`${Style['td-field']} ${Style['td-field__file']}`}>
                <InfoTextTooltip title={form_field?.get_translated_label(locale.toLowerCase())} />
            </td>
            <td className={Style['td-answer']}>
                {isNotValidAnswer(answer) ? (
                    <InfoTextTooltip title={t('ticket.common.noFile')} />
                ) : (
                    <a href={fileUrl} rel="noopener noreferrer" target="_blank" download>
                        <Button primary inverted label={t('ticket.common.action.download')} />
                    </a>
                )}
            </td>
        </tr>
    );
}

function RowImage({ form_field, answer, locale }) {
    const { t } = useTranslation();

    const imgUrl = React.useMemo(() => {
        return getWeezFileUrl(answer, window.weezConfig.aws.weez_bucket_form);
    }, [answer]);

    return (
        <tr>
            <td className={`${Style['td-field']} ${Style['td-field__file']}`}>
                <InfoTextTooltip title={form_field?.get_translated_label(locale.toLowerCase())} />
            </td>
            <td className={Style['td-answer']}>
                {isNotValidAnswer(answer) ? (
                    <InfoTextTooltip title={t('ticket.common.noImage')} />
                ) : (
                    <Tooltip margin={false} triggerOnHover title={t('ticket.common.action.download')}>
                        <a href={imgUrl} rel="noopener noreferrer" target="_blank" download>
                            <img alt="" className={Style['image']} src={imgUrl} />
                        </a>
                    </Tooltip>
                )}
            </td>
        </tr>
    );
}

function RowDivider({ form_field, locale }) {
    return (
        <tr>
            <td colSpan={2} className={Style['divider']}>
                <InfoTextTooltip title={form_field?.get_translated_label(locale.toLowerCase())} />
            </td>
        </tr>
    );
}

function RowGeneric({ form_field, answer, locale }) {
    const { t } = useTranslation();

    const _answer = React.useMemo(() => {
        if (isNotValidAnswer(answer)) {
            return '';
        }

        if (form_field.field_type === 'checkbox') {
            return Number(answer) ? t('ticket.manager.attendees.detail.filledForm.checked') : t('ticket.manager.attendees.detail.filledForm.notChecked');
        }

        if (form_field.field_type === 'country') {
            return capitalizeFirstLetter(translateCountry(locale, answer));
        }

        return answer;
    }, [form_field, answer, locale]);

    return (
        <tr>
            <td className={Style['td-field']}>
                <InfoTextTooltip title={form_field?.get_translated_label(locale.toLowerCase())} />
            </td>
            <td className={Style['td-answer']}>{isNotValidAnswer(_answer) ? <span>{`-`}</span> : <CopyToClipboardMinimal>{_answer}</CopyToClipboardMinimal>}</td>
        </tr>
    );
}

function Answer({ form_field, answer, ...rest }) {
    if (form_field.subtype) {
        const Component = FIELDS[form_field.subtype];
        if (Component) {
            return <Component form_field={form_field} answer={answer} {...rest} />;
        }
    }

    if (!FIELDS[form_field.field_type]) {
        return <RowGeneric form_field={form_field} answer={answer} {...rest} />;
    }

    const ComponentType = FIELDS[form_field.field_type];
    return <ComponentType form_field={form_field} answer={answer} {...rest} />;
}

export default function FormAnswers({ fields = [], answers = [], locale = 'en-GB' }) {
    return (
        <table className={Style['table-form-answers']}>
            <tbody>
                {fields.map(f => (
                    <Answer key={f.form_field.weez_id} form_field={f.form_field} answer={answers[f.form_field.weez_id]?.answer} locale={locale} />
                ))}
            </tbody>
        </table>
    );
}

FormAnswers.propTypes = {
    fields: PropTypes.array,
    answers: PropTypes.array,
    locale: PropTypes.string
};
